import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { TopExperienceDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { DrillDownModal } from '../shared/DrillDownModal';
import { BarChart3, TrendingUp, PieChart } from 'lucide-react';
import { ResponsivePie } from '@nivo/pie';
import { ChartWrapper } from '../shared/ChartWrapper';
import { CHART_THEMES, CHART_CONFIG } from '../config';
import { DarkTooltip } from '../shared/Tooltip';
import { formatNumber } from '../utils/dataUtils';
import { useCallback } from 'react';
import { useResponsiveChartConfig } from '../shared/useResponsiveChartConfig';
import { MetricCard } from '../overview/MetricCard';
import { palette } from '../shared/theme';
import { buildTopExperiencePieData } from './topExperiencesModal.utils';

interface TopExperiencesModalProps {
  data: TopExperienceDataDto[];
  isOpen: boolean;
  onClose: () => void;
  filters?: BaseAnalyticsFilter;
  onItemClick?: (experience: TopExperienceDataDto) => void;
}

export function TopExperiencesModal({
  data,
  isOpen,
  onClose,
  filters,
  onItemClick,
}: TopExperiencesModalProps) {
  const { isMobile } = useResponsiveChartConfig();
  const exportDataTransform = useCallback(
    (data: TopExperienceDataDto[]) =>
      data.map((experience, index) => ({
        rank: index + 1,
        sessionTitle: experience.experienceTitle,
        sessionCount: experience.sessionCount,
        totalMinutes: Math.round(experience.totalMinutes),
        averageDuration:
          experience.totalMinutes > 0
            ? Math.round(experience.totalMinutes / experience.sessionCount)
            : 0,
        percentage: experience.percentage,
        shareOfTotal: `${experience.percentage.toFixed(1)}%`,
      })),
    [data],
  );

  const renderChart = (
    data: TopExperienceDataDto[],
    showMetric: 'sessions' | 'minutes',
    isModalOpen = true,
    onItemClick?: (item: TopExperienceDataDto) => void,
  ) => {
    if (!data?.length) {
      return (
        <div className="flex h-full items-center justify-center text-gray-500">
          <p className="text-sm">No session data available</p>
        </div>
      );
    }

    const topExperience = data[0];
    const topExperienceShare = topExperience ? topExperience.percentage : 0;

    const pieData = buildTopExperiencePieData(data, showMetric);

    return (
      <div className="space-y-6">
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <MetricCard value={data.length} label="Session Types" accent={palette.indigo} />
          <MetricCard
            value={`${topExperienceShare.toFixed(1)}%`}
            label="Top Session"
            accent={CHART_CONFIG.COLORS.SECONDARY}
          />
        </div>

        <div className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)]">
          <div className="flex items-center justify-between gap-2 border-b border-[var(--anl-border)] p-4">
            <h3 className={`flex items-center gap-2 font-semibold text-[var(--anl-text)] ${isMobile ? 'text-sm' : 'text-lg'}`}>
              <PieChart className={`flex-shrink-0 text-[var(--anl-text-muted)] ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
              <span className="truncate">
                Session Distribution by {showMetric === 'minutes' ? 'Minutes' : 'Sessions'} (Top 50)
              </span>
            </h3>
            {onItemClick && (
              <div className="hidden items-center gap-1 text-sm text-[var(--anl-text-muted)] sm:flex">
                <span>Click session to see user details</span>
              </div>
            )}
          </div>
          <ChartWrapper height={isMobile ? 300 : 600} isModalOpen={isModalOpen}>
            <ResponsivePie
              data={pieData}
              margin={
                isMobile
                  ? { top: 20, right: 80, bottom: 40, left: 80 }
                  : { top: 40, right: 200, bottom: 80, left: 200 }
              }
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={2}
              activeOuterRadiusOffset={8}
              colors={{ datum: 'data.color' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
              arcLinkLabelsSkipAngle={isMobile ? 20 : 12}
              arcLinkLabelsTextColor="#e5e7eb"
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{
                from: 'color',
                modifiers: [['darker', 0.4]],
              }}
              arcLabelsSkipAngle={isMobile ? 20 : 12}
              arcLabelsTextColor="#ffffff"
              arcLabelsRadiusOffset={0.6}
              theme={{
                ...CHART_THEMES.default,
                labels: { text: { fontSize: 13, fill: '#ffffff' } },
              }}
              onClick={
                onItemClick
                  ? pieSlice => {
                      const originalExperience = data.find(
                        exp => exp.experienceTitle === pieSlice.id,
                      );
                      if (originalExperience) {
                        onItemClick(originalExperience);
                      }
                    }
                  : undefined
              }
              tooltip={({ datum }) => (
                <DarkTooltip>
                  <div
                    className="mb-1 max-w-[240px] truncate text-xs text-[var(--anl-text-muted)]"
                    title={String(datum.id)}
                  >
                    {String(datum.id)}
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm">
                      <span className="text-[var(--anl-text-muted)]">Sessions:</span>{' '}
                      <span className="font-semibold">{datum.data.sessions}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-[var(--anl-text-muted)]">Minutes:</span>{' '}
                      <span className="font-semibold">
                        {formatNumber(Number(datum.data.minutes))}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-[var(--anl-text-muted)]">Avg Duration:</span>{' '}
                      <span className="font-semibold">
                        {datum.data.sessions > 0
                          ? formatNumber(
                              Number(Math.round(datum.data.minutes / datum.data.sessions)),
                            )
                          : 0}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-[var(--anl-text-muted)]">Share:</span>{' '}
                      <span className="font-semibold">{datum.data.percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                </DarkTooltip>
              )}
              animate={true}
              motionConfig="gentle"
            />
          </ChartWrapper>
        </div>
      </div>
    );
  };

  const renderTable = (
    data: TopExperienceDataDto[],
    onItemClick?: (item: TopExperienceDataDto) => void,
  ) => {
    if (!data?.length) {
      return (
        <div className="flex h-full items-center justify-center text-gray-500">
          <p className="text-sm">No session data available</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-2 py-3 text-left font-medium text-gray-700">Session</th>
              <th className="px-2 py-3 text-right font-medium text-gray-700">Sessions</th>
              <th className="px-2 py-3 text-right font-medium text-gray-700">Minutes</th>
              <th className="px-2 py-3 text-right font-medium text-gray-700">Avg Duration</th>
              <th className="px-2 py-3 text-right font-medium text-gray-700">Share</th>
            </tr>
          </thead>
          <tbody>
            {data.map((experience, index) => (
              <tr
                key={experience.experienceTitle}
                className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50"
                onClick={() => onItemClick?.(experience)}
              >
                <td className="px-2 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-cyan-100 text-xs font-medium text-cyan-700">
                      {index + 1}
                    </div>
                    <div>
                      <div
                        className="max-w-xs truncate font-medium text-gray-900"
                        title={experience.experienceTitle}
                      >
                        {experience.experienceTitle}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-3 text-right font-medium text-gray-900">
                  {experience.sessionCount}
                </td>
                <td className="px-2 py-3 text-right text-gray-600">
                  {formatNumber(Number(Math.round(experience.totalMinutes)))}
                </td>
                <td className="px-2 py-3 text-right text-gray-600">
                  {experience.totalMinutes > 0
                    ? formatNumber(
                        Number(Math.round(experience.totalMinutes / experience.sessionCount)),
                      )
                    : 0}
                </td>
                <td className="px-2 py-3 text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <span className="font-medium text-gray-900">
                      {experience.percentage.toFixed(1)}%
                    </span>
                    <TrendingUp className="h-3 w-3 text-green-600" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <DrillDownModal
      title="Top Sessions"
      data={data}
      isOpen={isOpen}
      onClose={onClose}
      filters={filters}
      onItemClick={onItemClick}
      exportDataTransform={exportDataTransform}
      exportFileName="top-sessions"
      icon={<BarChart3 className="h-6 w-6" />}
    >
      {({ data, activeView, showMetric, onItemClick }) =>
        activeView === 'chart'
          ? renderChart(data, showMetric, isOpen, onItemClick)
          : renderTable(data, onItemClick)
      }
    </DrillDownModal>
  );
}
